import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Flask, CaretRight, TreeStructure, PencilSimple, ClipboardText, Copy } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import DistintaTree from "../components/DistintaTree";

const TYPE_COLOR = {
  materia_prima: "#457B9D",
  semilavorato: "#1D3557",
  prodotto_finito: "#2A9D8F",
  imballaggio: "#F4A261",
};

const TYPE_LABEL = {
  materia_prima: "MP",
  semilavorato: "SL",
  prodotto_finito: "PF",
  imballaggio: "IMB",
};

export default function Formule() {
  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [exploding, setExploding] = useState(null); // formula being exploded
  const [explodeMultiplier, setExplodeMultiplier] = useState(1);
  const [instructionsFor, setInstructionsFor] = useState(null);
  const [active, setActive] = useState("base"); // "base" | "finiti"
  const [categoryFilter, setCategoryFilter] = useState("");
  const [costs, setCosts] = useState([]);
  const [form, setForm] = useState(emptyForm("base"));

  const costById = (id) => costs.find((c) => c.formula_id === id);

  function emptyForm(scope = "base") {
    return {
      name: "",
      output_product_id: "",
      output_quantity: scope === "base" ? 1100 : 1,
      output_unit: scope === "base" ? "L" : "pz",
      final_volume: "",
      min_batch_quantity: "",
      components: [],
      notes: "",
      preparation_instructions: "",
      packaging_instructions: "",
    };
  }

  const load = useCallback(async () => {
    try {
      const [f, p, c] = await Promise.all([
        api.get("/formulas"),
        api.get("/products"),
        api.get("/formulas/costs/all"),
      ]);
      setFormulas(f.data);
      setProducts(p.data);
      setCosts(c.data);
    } catch (err) {
      // 401 is handled globally by the axios interceptor (redirect to /login).
      // Other errors show a toast.
      if (err?.response?.status !== 401) {
        toast.error(formatApiError(err));
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const outputCandidates = products.filter((p) =>
    ["semilavorato", "prodotto_finito"].includes(p.type)
  );
  const componentCandidates = products.filter((p) =>
    ["materia_prima", "semilavorato", "imballaggio"].includes(p.type)
  );

  const productById = (id) => products.find((p) => p.id === id);

  // Filter formulas by active tab based on output product type
  const tabFormulas = formulas.filter((f) => {
    const out = productById(f.output_product_id);
    if (!out) return active === "base"; // orphans go to "base" by default
    if (active === "base") return out.type === "semilavorato";
    return out.type === "prodotto_finito";
  });

  // Categories available in the active tab (taken from each formula's output product)
  const tabCategories = Array.from(
    new Set(
      tabFormulas
        .map((f) => (productById(f.output_product_id)?.category || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "it"));

  // Apply category filter on top of the tab filter
  const visibleFormulas = categoryFilter
    ? tabFormulas.filter(
        (f) => (productById(f.output_product_id)?.category || "") === categoryFilter
      )
    : tabFormulas;

  // Output candidates filtered by active tab when creating
  const formScope = active; // "base" | "finiti"
  const formOutputCandidates = outputCandidates.filter((p) =>
    formScope === "base" ? p.type === "semilavorato" : p.type === "prodotto_finito"
  );

  const addComponent = () => {
    setForm({
      ...form,
      components: [
        ...form.components,
        { product_id: "", quantity: 0, unit: "" },
      ],
    });
  };

  const updateComponent = (i, key, value) => {
    const next = [...form.components];
    next[i] = { ...next[i], [key]: value };
    // When product changes, reset unit to product's default unit
    if (key === "product_id") {
      const p = products.find((x) => x.id === value);
      next[i].unit = p?.unit || "";
    }
    setForm({ ...form, components: next });
  };

  const removeComponent = (i) => {
    setForm({ ...form, components: form.components.filter((_, idx) => idx !== i) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.output_product_id) return toast.error("Seleziona il prodotto di output");
    if (form.components.length === 0) return toast.error("Aggiungi almeno un componente");

    // Validate each component row
    for (let i = 0; i < form.components.length; i++) {
      const c = form.components[i];
      if (!c.product_id) {
        return toast.error(`Riga componente #${i + 1}: seleziona un prodotto`);
      }
      if (!products.find((x) => x.id === c.product_id)) {
        return toast.error(`Riga componente #${i + 1}: prodotto non trovato`);
      }
      const q = Number(c.quantity);
      if (!q || q <= 0) {
        return toast.error(`Riga componente #${i + 1}: quantità deve essere > 0`);
      }
    }

    const enriched = form.components.map((c) => {
      const p = products.find((x) => x.id === c.product_id);
      return {
        product_id: p.id,
        product_name: p.name,
        product_barcode: p.barcode,
        product_type: p.type,
        quantity: Number(c.quantity),
        unit: c.unit || p.unit,
      };
    });
    try {
      if (editingId) {
        await api.put(`/formulas/${editingId}`, {
          name: form.name,
          output_product_id: form.output_product_id,
          output_quantity: Number(form.output_quantity),
          output_unit: form.output_unit,
          final_volume:
            form.final_volume === "" ? null : Number(form.final_volume),
          min_batch_quantity:
            form.min_batch_quantity === "" ? null : Number(form.min_batch_quantity),
          components: enriched,
          notes: form.notes,
          preparation_instructions: form.preparation_instructions || null,
          packaging_instructions: form.packaging_instructions || null,
        });
        toast.success("Distinta base aggiornata");
      } else {
        await api.post("/formulas", {
          name: form.name,
          output_product_id: form.output_product_id,
          output_quantity: Number(form.output_quantity),
          output_unit: form.output_unit,
          final_volume:
            form.final_volume === "" ? null : Number(form.final_volume),
          min_batch_quantity:
            form.min_batch_quantity === "" ? null : Number(form.min_batch_quantity),
          components: enriched,
          notes: form.notes,
          preparation_instructions: form.preparation_instructions || null,
          packaging_instructions: form.packaging_instructions || null,
        });
        toast.success("Distinta base creata");
      }
      setCreating(false);
      setEditingId(null);
      setForm(emptyForm(active));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (f) => {
    // Switch to correct tab based on output product type so editing list is consistent
    const out = productById(f.output_product_id);
    if (out) {
      setActive(out.type === "prodotto_finito" ? "finiti" : "base");
    }
    setForm({
      name: f.name,
      output_product_id: f.output_product_id,
      output_quantity: f.output_quantity,
      output_unit: f.output_unit,
      final_volume: f.final_volume ?? "",
      min_batch_quantity: f.min_batch_quantity ?? "",
      components: f.components.map((c) => ({
        product_id: c.product_id,
        quantity: c.quantity,
        unit: c.unit || "",
      })),
      notes: f.notes || "",
      preparation_instructions: f.preparation_instructions || "",
      packaging_instructions: f.packaging_instructions || "",
    });
    setEditingId(f.id);
    setCreating(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la distinta base?")) return;
    try {
      await api.delete(`/formulas/${id}`);
      toast.success("Distinta base eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicate = async (f) => {
    if (!window.confirm(`Duplicare la formula "${f.name}"?\n\nVerrà creata una copia con il suffisso " - Copia" che potrai poi rinominare e modificare.`)) return;
    try {
      const { data } = await api.post(`/formulas/${f.id}/duplicate`);
      toast.success(`Formula duplicata: ${data.name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="formule-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            {active === "base"
              ? "Formule Base · Batch 1100 L · Multi-livello"
              : "Distinte Prodotti Finiti · Imbottigliamento & Confezionamento"}
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            {active === "base" ? "Formule Base" : "Prodotti Finiti"}
          </h1>
        </div>
        <button
          onClick={() => {
            setForm(emptyForm(active));
            setEditingId(null);
            setCreating(true);
          }}
          data-testid="new-formula-btn"
          className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
        >
          <Plus size={14} weight="bold" />{" "}
          {active === "base" ? "Nuova Formula Base" : "Nuovo Prodotto Finito"}
        </button>
      </div>

      <div className="border-b border-[#E5E7EB] mb-6 flex gap-1">
        {[
          { key: "base", label: "Formule Base" },
          { key: "finiti", label: "Prodotti Finiti" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setActive(t.key);
              setExpanded(null);
              setCategoryFilter("");
            }}
            data-testid={`formula-tab-${t.key}`}
            className={`px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              active === t.key
                ? "border-[#1D3557] text-[#0A0A0A]"
                : "border-transparent text-[#4B5563] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}{" "}
            <span className="ml-2 font-mono-data text-[10px] text-[#4B5563]">
              {
                formulas.filter((f) => {
                  const out = productById(f.output_product_id);
                  if (!out) return t.key === "base";
                  return t.key === "base"
                    ? out.type === "semilavorato"
                    : out.type === "prodotto_finito";
                }).length
              }
            </span>
          </button>
        ))}
      </div>

      {tabCategories.length > 0 && (
        <div className="flex items-center gap-3 mb-4" data-testid="formula-category-filter-row">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
            Filtra per categoria
          </span>
          <select
            data-testid="formula-category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none min-w-[220px]"
          >
            <option value="">Tutte le categorie ({tabCategories.length})</option>
            {tabCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {categoryFilter && (
            <button
              onClick={() => setCategoryFilter("")}
              data-testid="formula-clear-category-filter"
              className="text-xs text-[#4B5563] hover:text-[#E63946] underline"
            >
              Rimuovi filtro
            </button>
          )}
          <span className="ml-auto text-xs font-mono-data text-[#4B5563]">
            {visibleFormulas.length} formul{visibleFormulas.length === 1 ? "a" : "e"}
          </span>
        </div>
      )}

      {visibleFormulas.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Flask size={32} weight="duotone" color="#1D3557" className="mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">
            {categoryFilter
              ? `Nessuna formula nella categoria "${categoryFilter}".`
              : active === "base"
              ? "Nessuna formula base. Crea la prima ricetta da batch 1100 L che produce un semilavorato."
              : "Nessuna distinta prodotto finito. Crea la prima per imbottigliare/confezionare un semilavorato."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleFormulas.map((f) => {
            const open = expanded === f.id;
            const outProd = productById(f.output_product_id);
            const outCategory = outProd?.category || "";
            return (
              <div
                key={f.id}
                className="bg-white border border-[#E5E7EB]"
                data-testid={`formula-row-${f.id}`}
              >
                <div
                  onClick={() => setExpanded(open ? null : f.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-[#F9FAFB] transition-colors text-left cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <CaretRight
                      size={16}
                      weight="bold"
                      className="text-[#4B5563] transition-transform"
                      style={{ transform: open ? "rotate(90deg)" : "none" }}
                    />
                    <div>
                      <div className="font-display font-semibold text-base text-[#0A0A0A] flex items-center gap-2">
                        {f.name}
                        {outCategory && (
                          <span
                            className="inline-block px-2 py-0.5 text-[10px] font-mono-data font-semibold bg-[#EEF2F7] text-[#1D3557] border border-[#1D3557]/20"
                            data-testid={`formula-category-badge-${f.id}`}
                            title={`Categoria: ${outCategory}`}
                          >
                            {outCategory}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#4B5563] mt-0.5">
                        Output: <span className="font-mono-data">{f.output_product_name}</span> ·{" "}
                        <span className="font-mono-data">{f.output_quantity} {f.output_unit}</span>
                        {f.final_volume && f.final_volume !== f.output_quantity && (
                          <span className="ml-2 font-mono-data text-[#1D3557] font-semibold">
                            · Vol. finale {f.final_volume} L
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono-data text-xs text-[#4B5563]">
                      {f.components.length} componenti
                    </span>
                    {costById(f.id) && costById(f.id).total_cost > 0 && (
                      <span
                        className="font-mono-data text-xs text-[#2A9D8F] font-semibold bg-[#E6F4F1] px-2 py-1 border border-[#2A9D8F]/30"
                        title={`€ ${costById(f.id).cost_per_output_unit.toFixed(4)} per ${f.output_unit}`}
                        data-testid={`formula-cost-${f.id}`}
                      >
                        € {costById(f.id).total_cost.toFixed(2)}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setInstructionsFor(f);
                      }}
                      data-testid={`instructions-formula-${f.id}`}
                      title="Scheda d'istruzione"
                      className="text-[#4B5563] hover:text-[#1D3557] p-1"
                    >
                      <ClipboardText size={16} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExplodeMultiplier(1);
                        setExploding(f);
                      }}
                      data-testid={`explode-formula-${f.id}`}
                      title="Distinta base esplosa"
                      className="text-[#4B5563] hover:text-[#1D3557] p-1"
                    >
                      <TreeStructure size={16} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(f);
                      }}
                      data-testid={`edit-formula-${f.id}`}
                      title="Modifica distinta"
                      className="text-[#4B5563] hover:text-[#1D3557] p-1"
                    >
                      <PencilSimple size={16} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicate(f);
                      }}
                      data-testid={`duplicate-formula-${f.id}`}
                      title="Duplica distinta"
                      className="text-[#4B5563] hover:text-[#2A9D8F] p-1"
                    >
                      <Copy size={16} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(f.id);
                      }}
                      data-testid={`delete-formula-${f.id}`}
                      className="text-[#4B5563] hover:text-[#E63946] p-1"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] p-5">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
                      Componenti richiesti
                    </div>
                    {(() => {
                      // Volume summary (only meaningful when output is in L)
                      if (f.output_unit !== "L") return null;
                      // Sum every component into litres using density when needed.
                      // Track also solids without density so we can warn the user.
                      let componentsLitres = 0;
                      const missingDensity = [];
                      for (const c of f.components) {
                        const u = (c.unit || "").toLowerCase();
                        const q = Number(c.quantity || 0);
                        if (q <= 0 || u === "pz") continue;
                        if (u === "l") {
                          componentsLitres += q;
                        } else if (u === "ml") {
                          componentsLitres += q / 1000;
                        } else if (u === "kg" || u === "g") {
                          const prod = productById(c.product_id);
                          const density = Number(prod?.density || 0); // kg/L
                          const massKg = u === "kg" ? q : q / 1000;
                          if (density > 0) {
                            componentsLitres += massKg / density;
                          } else {
                            missingDensity.push(c.product_name || prod?.name || "");
                          }
                        }
                      }
                      const finalVol = f.final_volume ?? f.output_quantity;
                      const water = finalVol - componentsLitres;
                      if (componentsLitres === 0 && !f.final_volume) return null;
                      return (
                        <>
                        <div className="grid grid-cols-3 gap-3 mb-2">
                          <div className="bg-white border border-[#E5E7EB] p-3">
                            <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                              Volume componenti (L)
                            </div>
                            <div className="font-mono-data text-lg font-semibold text-[#0A0A0A]">
                              {componentsLitres.toFixed(2)} L
                            </div>
                            <div className="text-[10px] text-[#4B5563] mt-0.5">
                              liquidi + solidi (massa÷densità)
                            </div>
                          </div>
                          <div className="bg-[#EEF2F7] border border-[#1D3557]/30 p-3">
                            <div className="text-[10px] uppercase tracking-wider text-[#1D3557]">
                              Volume Finale
                            </div>
                            <div className="font-mono-data text-lg font-bold text-[#1D3557]">
                              {Number(finalVol).toFixed(2)} L
                            </div>
                          </div>
                          <div
                            className={`border p-3 ${
                              water > 0
                                ? "bg-[#E8F4FD] border-[#457B9D]/40"
                                : water < 0
                                ? "bg-[#FBEDEE] border-[#E63946]/40"
                                : "bg-white border-[#E5E7EB]"
                            }`}
                          >
                            <div className="text-[10px] uppercase tracking-wider text-[#457B9D]">
                              Acqua / diluente
                            </div>
                            <div
                              className={`font-mono-data text-lg font-semibold ${
                                water < 0 ? "text-[#E63946]" : "text-[#457B9D]"
                              }`}
                              title={
                                water < 0
                                  ? "I componenti superano il volume finale"
                                  : "Da aggiungere fino a volume finale"
                              }
                            >
                              {water >= 0 ? "+" : ""}
                              {water.toFixed(2)} L
                            </div>
                          </div>
                        </div>
                        {missingDensity.length > 0 && (
                          <div className="bg-[#FFF8E7] border border-[#F4A261]/40 p-2 mb-4 text-xs text-[#4B5563]">
                            <span className="font-semibold text-[#F4A261]">⚠ Volume parziale:</span>{" "}
                            manca il <b>peso specifico</b> per:{" "}
                            <span className="font-mono-data">
                              {[...new Set(missingDensity)].join(", ")}
                            </span>
                            . Inseriscilo dal{" "}
                            <a href="/listino" className="underline text-[#1D3557] font-semibold">
                              Listino MP
                            </a>{" "}
                            per ottenere il calcolo esatto dell'acqua.
                          </div>
                        )}
                        </>
                      );
                    })()}
                    <table className="w-full bg-white border border-[#E5E7EB]">
                      <thead className="bg-[#F3F4F6]">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                          <th className="px-4 py-2 font-semibold">Tipo</th>
                          <th className="px-4 py-2 font-semibold">Componente</th>
                          <th className="px-4 py-2 font-semibold">EAN-13</th>
                          <th className="px-4 py-2 font-semibold text-right">Quantità</th>
                          <th className="px-4 py-2 font-semibold text-right">Costo unit.</th>
                          <th className="px-4 py-2 font-semibold text-right">Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {f.components.map((c, idx) => {
                          const costRow = costById(f.id)?.breakdown?.find(
                            (b) => b.product_id === c.product_id
                          );
                          return (
                          <tr
                            key={idx}
                            className="border-t border-[#E5E7EB]"
                          >
                            <td className="px-4 py-2">
                              <span
                                className="inline-block px-2 py-0.5 text-[10px] font-mono-data font-semibold text-white"
                                style={{ background: TYPE_COLOR[c.product_type] }}
                              >
                                {TYPE_LABEL[c.product_type]}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-[#0A0A0A]">
                              {c.product_name}
                            </td>
                            <td className="px-4 py-2 font-mono-data text-xs text-[#4B5563]">
                              {c.product_barcode}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-sm font-semibold text-[#0A0A0A]">
                              {c.quantity} {c.unit}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-xs text-[#4B5563]">
                              {costRow && costRow.unit_cost > 0
                                ? `€ ${costRow.unit_cost.toFixed(4)}`
                                : "—"}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-sm font-semibold text-[#2A9D8F]">
                              {costRow && costRow.line_total > 0
                                ? `€ ${costRow.line_total.toFixed(2)}`
                                : "—"}
                            </td>
                          </tr>
                          );
                        })}
                        {costById(f.id) && costById(f.id).total_cost > 0 && (
                          <tr className="border-t-2 border-[#1D3557] bg-[#F0F4F8]">
                            <td colSpan={4} className="px-4 py-3 text-right text-xs uppercase tracking-wider font-semibold text-[#1D3557]">
                              Costo Totale Formula · {f.output_quantity} {f.output_unit}
                            </td>
                            <td className="px-4 py-3 text-right font-mono-data text-xs text-[#4B5563]">
                              € {costById(f.id).cost_per_output_unit.toFixed(4)} / {f.output_unit}
                            </td>
                            <td className="px-4 py-3 text-right font-mono-data text-base font-bold text-[#1D3557]">
                              € {costById(f.id).total_cost.toFixed(2)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {f.notes && (
                      <div className="mt-4 text-xs text-[#4B5563]">
                        <span className="font-semibold uppercase tracking-wider">Note:</span>{" "}
                        {f.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={creating}
        onOpenChange={(v) => {
          setCreating(v);
          if (!v) setEditingId(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-formula-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId
                ? active === "base"
                  ? "Modifica Formula Base"
                  : "Modifica Distinta Prodotto Finito"
                : active === "base"
                ? "Nuova Formula Base"
                : "Nuovo Prodotto Finito"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Nome formula *
              </label>
              <input
                required
                data-testid="formula-name-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Prodotto di output *
                </label>
                <Select
                  value={form.output_product_id}
                  onValueChange={(v) => {
                    const p = products.find((x) => x.id === v);
                    setForm({
                      ...form,
                      output_product_id: v,
                      output_unit: p?.unit || form.output_unit,
                    });
                  }}
                >
                  <SelectTrigger data-testid="formula-output-select" className="rounded-none">
                    <SelectValue placeholder={active === "base" ? "Seleziona semilavorato" : "Seleziona prodotto finito"} />
                  </SelectTrigger>
                  <SelectContent>
                    {formOutputCandidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        [{TYPE_LABEL[p.type]}] {p.name} · {p.unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Output ({form.output_unit})
                </label>
                <input
                  type="number"
                  data-testid="formula-output-quantity-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                  value={form.output_quantity}
                  onChange={(e) => setForm({ ...form, output_quantity: e.target.value })}
                />
              </div>
            </div>

            {form.output_unit === "L" && (
              <div className="bg-[#EEF2F7] border border-[#1D3557]/20 p-3">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] block mb-1">
                  💧 Volume Finale dopo diluizione (opz.)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    data-testid="formula-final-volume-input"
                    className="flex-1 border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={form.final_volume}
                    placeholder={`Vuoto = pari all'output (${form.output_quantity} L)`}
                    onChange={(e) => setForm({ ...form, final_volume: e.target.value })}
                  />
                  <span className="text-xs font-mono-data text-[#4B5563]">L</span>
                </div>
                <p className="text-[10px] text-[#4B5563] mt-1">
                  Se dosi gli ingredienti e poi aggiungi acqua/diluente fino a un volume target, inseriscilo qui. Il sistema calcolerà l'acqua da aggiungere = Volume Finale − somma componenti in litri.
                </p>
              </div>
            )}

            <div className="bg-[#FEF5EB] border border-[#F4A261]/30 p-3">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#F4A261] block mb-1">
                ⚠ Batch minimo emergenza (opz.)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Lascia vuoto per imporre solo batch interi"
                  data-testid="formula-min-batch-input"
                  className="flex-1 border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261] font-mono-data"
                  value={form.min_batch_quantity}
                  onChange={(e) => setForm({ ...form, min_batch_quantity: e.target.value })}
                />
                <span className="text-xs font-mono-data text-[#4B5563]">
                  {form.output_unit}
                </span>
              </div>
              <p className="text-[10px] text-[#4B5563] mt-1">
                Quantità minima producibile in modalità emergenza (es. 700 L per batch standard 1100 L). Se vuoto, sono ammessi solo batch interi.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
                  Componenti (MP + SL)
                </label>
                <button
                  type="button"
                  onClick={addComponent}
                  data-testid="add-component-btn"
                  className="text-xs font-display font-semibold uppercase tracking-wider text-[#1D3557] hover:text-[#2A4B7C] flex items-center gap-1"
                >
                  <Plus size={12} weight="bold" /> Aggiungi
                </button>
              </div>
              {form.components.length === 0 && (
                <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563] text-center">
                  Nessun componente. Clicca "Aggiungi" per inserirne uno.
                </div>
              )}
              <div className="space-y-2">
                {form.components.map((c, i) => {
                  const compProd = products.find((x) => x.id === c.product_id);
                  const baseUnit = compProd?.unit || "";
                  // Unit options: always include pz (pieces) so that any component
                  // can be dosed by piece (es. fusti, sacchi, bottiglie, rotoli).
                  const unitOptions = (() => {
                    const u = (baseUnit || "").toLowerCase();
                    if (u === "l" || u === "ml") return ["L", "ml", "pz"];
                    if (u === "kg" || u === "g") return ["kg", "g", "pz"];
                    if (u === "m") return ["m", "pz"];
                    if (u === "pz") return ["pz"];
                    return baseUnit ? [baseUnit, "pz"] : ["L", "ml", "kg", "g", "m", "pz"];
                  })();
                  return (
                  <div key={i} className="flex gap-2 items-center" data-testid={`component-row-${i}`}>
                    <div className="flex-1">
                      <Select
                        value={c.product_id}
                        onValueChange={(v) => updateComponent(i, "product_id", v)}
                      >
                        <SelectTrigger className="rounded-none" data-testid={`component-select-${i}`}>
                          <SelectValue placeholder="Seleziona componente" />
                        </SelectTrigger>
                        <SelectContent>
                          {componentCandidates.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              [{TYPE_LABEL[p.type]}] {p.name} · {p.unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <input
                      type="number"
                      step="0.001"
                      placeholder="Qtà"
                      data-testid={`component-qty-${i}`}
                      className="w-24 border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                      value={c.quantity}
                      onChange={(e) => updateComponent(i, "quantity", e.target.value)}
                    />
                    <select
                      data-testid={`component-unit-${i}`}
                      value={c.unit || baseUnit}
                      onChange={(e) => updateComponent(i, "unit", e.target.value)}
                      disabled={!c.product_id}
                      className="border border-[#E5E7EB] bg-white px-2 py-2 text-sm font-mono-data outline-none focus:ring-2 focus:ring-[#1D3557] disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                      title="Cambia unità di misura per questo componente (es. usa g invece di kg per quantità piccole)"
                    >
                      {unitOptions.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeComponent(i)}
                      className="text-[#4B5563] hover:text-[#E63946] p-2"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Note
              </label>
              <textarea
                rows={2}
                data-testid="formula-notes-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-3 flex items-center gap-2">
                <ClipboardText size={14} weight="bold" /> Scheda d'istruzione
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                    Istruzioni di Preparazione (chimica)
                  </label>
                  <textarea
                    rows={5}
                    data-testid="formula-prep-instructions-input"
                    placeholder="1. Versare X litri di acqua demineralizzata...&#10;2. Sciogliere Y kg di CD3 mescolando lentamente...&#10;3. Aggiungere..."
                    className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={form.preparation_instructions}
                    onChange={(e) => setForm({ ...form, preparation_instructions: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                    Istruzioni di Confezionamento
                  </label>
                  <textarea
                    rows={4}
                    data-testid="formula-pack-instructions-input"
                    placeholder="1. Riempire bottiglie da 1 L...&#10;2. Applicare tappo ad induzione...&#10;3. Etichettare e inserire in scatola 2A..."
                    className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={form.packaging_instructions}
                    onChange={(e) => setForm({ ...form, packaging_instructions: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setEditingId(null);
                }}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
              >
                Annulla
              </button>
              <button
                type="submit"
                data-testid="submit-formula-btn"
                className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
              >
                {editingId ? "Salva Modifiche" : "Crea Distinta"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DistintaEsplosaDialog
        formula={exploding}
        multiplier={explodeMultiplier}
        setMultiplier={setExplodeMultiplier}
        formulas={formulas}
        onClose={() => setExploding(null)}
      />

      <Dialog open={!!instructionsFor} onOpenChange={(v) => !v && setInstructionsFor(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="instructions-dialog">
          {instructionsFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                  <ClipboardText size={20} weight="bold" /> Scheda · {instructionsFor.name}
                </DialogTitle>
              </DialogHeader>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 mb-4 text-xs">
                Output: <span className="font-mono-data font-semibold">{instructionsFor.output_quantity} {instructionsFor.output_unit}</span> ·
                Componenti: <span className="font-mono-data font-semibold">{instructionsFor.components.length}</span>
                {instructionsFor.min_batch_quantity && (
                  <> · Min batch emergenza: <span className="font-mono-data font-semibold text-[#F4A261]">{instructionsFor.min_batch_quantity} {instructionsFor.output_unit}</span></>
                )}
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-2">
                    Componenti distinta
                  </div>
                  <table className="w-full bg-white border border-[#E5E7EB]">
                    <tbody>
                      {instructionsFor.components.map((c, i) => (
                        <tr key={i} className="border-t border-[#E5E7EB] first:border-t-0">
                          <td className="px-3 py-1.5 text-sm">{c.product_name}</td>
                          <td className="px-3 py-1.5 text-right font-mono-data text-sm font-semibold">{c.quantity} {c.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-2">
                    Preparazione (chimica)
                  </div>
                  {instructionsFor.preparation_instructions ? (
                    <pre className="bg-white border border-[#E5E7EB] p-4 text-sm text-[#0A0A0A] font-mono-data whitespace-pre-wrap">{instructionsFor.preparation_instructions}</pre>
                  ) : (
                    <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563] text-center">
                      Nessuna istruzione di preparazione. Modifica la distinta per aggiungerla.
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-2">
                    Confezionamento
                  </div>
                  {instructionsFor.packaging_instructions ? (
                    <pre className="bg-white border border-[#E5E7EB] p-4 text-sm text-[#0A0A0A] font-mono-data whitespace-pre-wrap">{instructionsFor.packaging_instructions}</pre>
                  ) : (
                    <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563] text-center">
                      Nessuna istruzione di confezionamento.
                    </div>
                  )}
                </div>

                {instructionsFor.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">Note</div>
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 text-xs">{instructionsFor.notes}</div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="px-5 py-2.5 border border-[#1D3557] text-[#1D3557] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
                >
                  Stampa
                </button>
                <button
                  type="button"
                  onClick={() => setInstructionsFor(null)}
                  className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
                >
                  Chiudi
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Recursively explode a formula into its leaves (MP + IMB) and intermediate SL nodes.
 * For each component with required quantity q:
 *   - If there is a sub-formula producing that component, scale by q / sub.output_quantity and recurse.
 *   - Else treat as leaf.
 */
function explodeFormula(formula, multiplier, formulasById, depth = 0, path = []) {
  const tree = [];
  const leaves = {}; // key: product_id -> aggregated
  const intermediates = {}; // SL that are produced via sub-formulas

  for (const c of formula.components) {
    const required = c.quantity * multiplier;
    const node = {
      ...c,
      required_quantity: required,
      depth,
      children: [],
      isLeaf: true,
    };

    if (c.product_type === "semilavorato" && formulasById[c.product_id]) {
      if (path.includes(c.product_id)) {
        // Cycle detected; treat as leaf to avoid infinite recursion
      } else {
        const sub = formulasById[c.product_id];
        const subMultiplier = required / sub.output_quantity;
        const { tree: subTree, leaves: subLeaves, intermediates: subInter } = explodeFormula(
          sub,
          subMultiplier,
          formulasById,
          depth + 1,
          [...path, c.product_id]
        );
        node.children = subTree;
        node.isLeaf = false;
        node.sub_multiplier = subMultiplier;

        // Aggregate sub-leaves
        Object.values(subLeaves).forEach((l) => {
          if (!leaves[l.product_id]) leaves[l.product_id] = { ...l, required_quantity: 0 };
          leaves[l.product_id].required_quantity += l.required_quantity;
        });
        // Aggregate intermediate SL needs
        Object.values(subInter).forEach((l) => {
          if (!intermediates[l.product_id]) intermediates[l.product_id] = { ...l, required_quantity: 0 };
          intermediates[l.product_id].required_quantity += l.required_quantity;
        });
        // Track this SL itself as intermediate demand
        if (!intermediates[c.product_id])
          intermediates[c.product_id] = {
            product_id: c.product_id,
            product_name: c.product_name,
            product_barcode: c.product_barcode,
            product_type: c.product_type,
            unit: c.unit,
            required_quantity: 0,
          };
        intermediates[c.product_id].required_quantity += required;
      }
    }

    if (node.isLeaf) {
      // Leaf: MP, IMB, or SL without sub-formula
      if (!leaves[c.product_id])
        leaves[c.product_id] = {
          product_id: c.product_id,
          product_name: c.product_name,
          product_barcode: c.product_barcode,
          product_type: c.product_type,
          unit: c.unit,
          required_quantity: 0,
        };
      leaves[c.product_id].required_quantity += required;
    }

    tree.push(node);
  }

  return { tree, leaves, intermediates };
}

function DistintaEsplosaDialog({ formula, multiplier, setMultiplier, formulas, onClose }) {
  const open = !!formula;
  if (!formula) return null;

  const formulasById = {};
  formulas.forEach((f) => {
    formulasById[f.output_product_id] = f;
  });

  const { tree, leaves, intermediates } = explodeFormula(formula, multiplier, formulasById);
  const leavesArr = Object.values(leaves).sort((a, b) => {
    const order = { materia_prima: 0, imballaggio: 1, semilavorato: 2, prodotto_finito: 3 };
    return order[a.product_type] - order[b.product_type];
  });
  const intermediatesArr = Object.values(intermediates);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="distinta-esplosa-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Distinta Base Esplosa · {formula.name}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
              Output
            </div>
            <div className="font-display font-semibold text-sm text-[#0A0A0A]">
              {formula.output_product_name} ·{" "}
              <span className="font-mono-data">
                {(formula.output_quantity * multiplier).toFixed(2)} {formula.output_unit}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
              Moltiplicatore
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={multiplier}
              onChange={(e) => setMultiplier(Number(e.target.value) || 0)}
              className="w-24 border border-[#E5E7EB] bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
              data-testid="explode-multiplier-input"
            />
          </div>
        </div>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
            Struttura Gerarchica
          </div>
          <div className="bg-white border border-[#E5E7EB] p-4">
            <DistintaTree
              rootName={formula.output_product_name}
              rootType="prodotto_finito"
              rootQuantity={formula.output_quantity * multiplier}
              rootUnit={formula.output_unit}
              tree={tree}
            />
          </div>
        </div>

        {intermediatesArr.length > 0 && (
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
              Semilavorati Intermedi da Produrre ({intermediatesArr.length})
            </div>
            <table className="w-full bg-white border border-[#E5E7EB]">
              <thead className="bg-[#F3F4F6]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                  <th className="px-3 py-2 font-semibold">Semilavorato</th>
                  <th className="px-3 py-2 font-semibold">EAN-13</th>
                  <th className="px-3 py-2 font-semibold text-right">Fabbisogno</th>
                </tr>
              </thead>
              <tbody>
                {intermediatesArr.map((l) => (
                  <tr key={l.product_id} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2 text-sm text-[#0A0A0A]">{l.product_name}</td>
                    <td className="px-3 py-2 font-mono-data text-xs text-[#4B5563]">
                      {l.product_barcode}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold text-[#0A0A0A]">
                      {l.required_quantity.toFixed(3)} {l.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
            Fabbisogno Totale Materie Prime + Imballaggi ({leavesArr.length})
          </div>
          <table className="w-full bg-white border border-[#E5E7EB]" data-testid="esplosa-leaves-table">
            <thead className="bg-[#F3F4F6]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-3 py-2 font-semibold">Tipo</th>
                <th className="px-3 py-2 font-semibold">Componente</th>
                <th className="px-3 py-2 font-semibold">EAN-13</th>
                <th className="px-3 py-2 font-semibold text-right">Quantità totale</th>
              </tr>
            </thead>
            <tbody>
              {leavesArr.map((l) => (
                <tr key={l.product_id} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-mono-data font-semibold text-white"
                      style={{ background: TYPE_COLOR[l.product_type] }}
                    >
                      {TYPE_LABEL[l.product_type]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-[#0A0A0A]">{l.product_name}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-[#4B5563]">
                    {l.product_barcode}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold text-[#0A0A0A]">
                    {l.required_quantity.toFixed(3)} {l.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
          >
            Chiudi
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

